import * as $ from 'jquery';
import * as Backbone from "backbone";
import * as Marionette from "backbone.marionette";
import {Options} from "../utils/decorators";
import {StripAlphaCharacters} from "../utils/currency-field";
import Vue from 'vue';
import {ApplePay, ApplePayConfig} from "../applepay/apple-pay";
import {PayPal, PayPalConfig} from "../paypal/paypal";
import createRequest from "../utils/create-request";

const Weglot = (<any>window).Weglot;

enum OrderStatus {
    INITIALIZED,
    PAYMENT,
    AUTHORIZE,
    AUTHORIZED,
    COMPLETED,
    CANCELED,
    ERROR
}

enum RecognitionStatus {
    INITIALIZED,
    SENDING,
    SENT,
}

enum TributeStatus {
    INITIALIZED,
    SENDING,
    SENT,
}

enum ReasonStatus {
    INITIALIZED,
    SENDING,
    SENT,
}

@Options({
    template: false,
    ui: {}
})
export default class OnePageGivingWidgetView extends Marionette.View<Backbone.Model>{
    config?: object;
    element?: any;
    instanceID?: number;
    fundID?: number;
    sourceCode?: number;
    prices?: object[];
    price?: number;
    isInputActive?: boolean;
    applePay: ApplePay;
    payPal: PayPal;
    private texts: any[];
    private recognition_endpoint: string;
    private tribute_endpoint: string;
    private reason_endpoint: string;

    constructor(options?: object, config?: object) {
        super(options);
        this.config = config || {};

        this.element = this.$el.get(0);

        const urlParams = new URLSearchParams(window.location.search);
        const sourceCodeParam = urlParams.get('sourcenumber');

        this.instanceID = this.element.getAttribute('data-id');
        this.fundID = this.element.getAttribute('data-fund-id') || 0;
        this.sourceCode = sourceCodeParam || this.element.getAttribute('data-sourcecode') || 0;

        if(this.element.getAttribute('data-prices')) {
            this.prices = [];
            let prices = this.element.getAttribute('data-prices')
                .split('|');

            for (let index = 0; index < prices.length; index++) {

                let displayPrice = prices[index];
                let dataPrice = parseInt(prices[index].trim().replace(/\D/g, ''));

                if (dataPrice != null && !isNaN(dataPrice)) {
                    this.prices.push({
                        displayPrice: displayPrice.trim(),
                        dataPrice: dataPrice
                    });
                }
            }

        } else {
            this.prices = [];
        }

    }

    onRender() {
        let currentLang = Weglot.getCurrentLang();

        if (currentLang === undefined || typeof currentLang === "undefined" || currentLang === "") {
            currentLang = "en";
        }

        let app = new Vue({
            data: {
                instanceID: this.instanceID,
                prices: this.prices,
                price: 0,
                fundID: this.fundID,
                sourceCode:  this.sourceCode,
                isInputActive: false,
                applePay: false,
                payPal: false,
                canMakeApplePayPaymentsWithActiveCard: false,
                orderStatus: OrderStatus.INITIALIZED,
                recognitionStatus: RecognitionStatus.INITIALIZED,
                tributeStatus: TributeStatus.INITIALIZED,
                reasonStatus: ReasonStatus.INITIALIZED,
                texts: [],
                recognition_endpoint: false,
                tribute_endpoint: false,
                reason_endpoint: false,
                recognition: 1,
                recognitionName: '',
                tributeSwitch: false,
                tribute: false,
                tributeName: '',
                tributeAddress: '',
                donationReason: '',
            },
            computed: {
                isDisabled() {
                    let value = parseFloat(this.selectedPrice);

                    return value <= 0 || isNaN(value);
                },
                isApplePayDisabled() {
                    return this.isDisabled || !this.applePay || !this.canMakeApplePayPaymentsWithActiveCard;
                },
                canApplePayMakePayments() {
                    return this.applePay && this.applePay.canMakePayments() && this.applePay.getAllowedLanguages().indexOf(currentLang.toLowerCase()) >= 0;
                },
                isPayPalDisabled() {
                    return this.isDisabled || !this.payPal || !this.payPal.isEnabled();
                },
                canPayPalMakePayments() {
                    return !!this.payPal;
                },
                sortedPrices: function() {
                    let p = this.prices.slice();
                    p.sort();

                    return p;
                },
                selectedPrice: {
                    get() {
                        this.updatePaymentData();
                        if (this.isInputActive) {
                            // Cursor is inside the input field. unformat display value for user
                            return this.price.toString()
                        } else {
                            // User is not modifying now. Format display value for user interface

                            if (this.price <= 0) {
                                return '';
                            } else {
                                return this.price.toLocaleString()
                            }
                        }
                    },
                    set(modifiedValue: string) {
                        this.price = StripAlphaCharacters(modifiedValue);
                        this.updatePaymentData();
                    }
                },
                link: function() {
                    let domain = window['DJANGO']['boDomain'].replace(/\/en/, '/' + currentLang);

                    domain += `/donate/${this.fundID}/add-to-basket/${this.price}`

                    if(this.sourcecode !== 0) {
                        domain += '?sourceNumber=' + this.sourceCode
                    }

                    return domain
                },
                shouldDisplayErrorScreen() {
                    return this.orderStatus === OrderStatus.ERROR;
                },
                shouldDisplayPaymentCanceledScreen() {
                    return this.orderStatus === OrderStatus.CANCELED;
                },
                shouldDisplayWorkingScreen() {
                    return (
                        this.orderStatus === OrderStatus.PAYMENT ||
                        this.orderStatus === OrderStatus.AUTHORIZE ||
                        this.orderStatus === OrderStatus.AUTHORIZED
                    );
                },
                shouldDisplayOrderCompleteScreen() {
                    return this.orderStatus === OrderStatus.COMPLETED;
                },
                shouldDisplayDefaultScreen() {
                    return this.orderStatus === OrderStatus.INITIALIZED
                },
                shouldDisplayRecognitionForm() {
                    return this.recognitionStatus === RecognitionStatus.INITIALIZED;
                },
                shouldDisplayRecognitionSending() {
                    return this.recognitionStatus === RecognitionStatus.SENDING;
                },
                shouldDisplayRecognitionSent() {
                    return this.recognitionStatus === RecognitionStatus.SENT;
                },
                shouldDisplayTributeForm() {
                    return this.tributeStatus === TributeStatus.INITIALIZED;
                },
                shouldDisplayTributeSending() {
                    return this.tributeStatus === TributeStatus.SENDING;
                },
                shouldDisplayTributeSent() {
                    return this.tributeStatus === TributeStatus.SENT;
                },
                shouldDisplayReasonForm() {
                    return this.reasonStatus === ReasonStatus.INITIALIZED;
                },
                shouldDisplayReasonSending() {
                    return this.reasonStatus === ReasonStatus.SENDING;
                },
                shouldDisplayReasonSent() {
                    return this.reasonStatus === ReasonStatus.SENT;
                }
            },
            methods: {
                select: function(price) {
                    this.selectedPrice = price.toString();
                },
                checkDisabled: function(e){

                    let value = parseFloat(this.selectedPrice);

                    if (value <= 0 || isNaN(value)) {
                        e.preventDefault();
                        return false;
                    }

                    return true;
                },
                updatePaymentData: function () {
                    this.payPal.setData({
                        fundID: this.fundID,
                        price: this.price,
                        sourceCode: this.sourceCode,
                    });
                    this.applePay.setData({
                        fundID: this.fundID,
                        price: this.price,
                        sourceCode: this.sourceCode,
                    });
                },
                startApplePayPayment: function (event) {
                    event.preventDefault();

                    if (!this.isApplePayDisabled) {
                        this.onPayment();

                        this.applePay.startPayment()
                    }
                },
                tryAgain: function(): void {
                    this.orderStatus = OrderStatus.INITIALIZED;
                },
                onAuthorizePayment: function (config?: ApplePayConfig|PayPalConfig): void {
                    this.orderStatus = OrderStatus.AUTHORIZE;
                },
                onAuthorizePaymentSuccess: function (data, config: ApplePayConfig|PayPalConfig): void {
                    if (data.result.dataLayer) {
                        // @ts-ignore
                        window.dataLayer = window.dataLayer || [];
                        // @ts-ignore
                        window.dataLayer.push(data.result.dataLayer);
                    }

                    this.orderStatus = OrderStatus.AUTHORIZED;
                    createRequest(config.urls.cleanup).then(() => {
                        this.orderStatus = OrderStatus.COMPLETED;
                    });
                },
                onAuthorizePaymentFail: function (data, config: ApplePayConfig|PayPalConfig): void {
                    createRequest(config.urls.cleanup).then(() => {
                        this.orderStatus = OrderStatus.ERROR;
                    });
                },
                onAuthorizePaymentError: function (reason, config: ApplePayConfig|PayPalConfig): void {
                    createRequest(config.urls.cleanup).then(() => {
                        this.orderStatus = OrderStatus.ERROR;
                    });
                },
                onPayment: function (config?: ApplePayConfig|PayPalConfig): void {
                    this.orderStatus = OrderStatus.PAYMENT;
                },
                onCancel: function (data, config: ApplePayConfig|PayPalConfig): void {
                    createRequest(config.urls.cleanup).then(() => {
                        this.orderStatus = OrderStatus.CANCELED;
                    });
                },
                onError: function (err, config: ApplePayConfig|PayPalConfig): void {
                    createRequest(config.urls.cleanup).then(() => {
                        this.orderStatus = OrderStatus.ERROR;
                    });
                },
                setApplePayInstance: function (applePay: ApplePay): void {
                    this.applePay = applePay;

                    if (this.applePay && this.applePay.canMakePayments()) {
                        applePay.canMakePaymentsWithActiveCard().then((result) => {
                            this.canMakeApplePayPaymentsWithActiveCard = result;
                        });
                    }
                },
                setPayPalInstance: function (payPal: PayPal): void {
                    this.payPal = payPal;
                },
                setTexts: function (texts: any[]): void {
                    this.texts = texts;
                },
                setRecognitionEndpoint: function (recognition_endpoint: string): void {
                    this.recognition_endpoint = recognition_endpoint;
                },
                setTributeEndpoint: function (tribute_endpoint: string): void {
                    this.tribute_endpoint = tribute_endpoint;
                },
                setReasonEndpoint: function (reason_endpoint: string): void {
                    this.reason_endpoint = reason_endpoint;
                },
                onRecognitionSubmit: function (event): void {
                    event.preventDefault();
                    this.recognitionStatus = RecognitionStatus.SENDING;

                    createRequest(this.recognition_endpoint, {
                        'recognition': this.recognition,
                        'form': {
                            'recognition_name': this.recognitionName
                        },
                    }).then((data) => {
                        this.recognitionStatus = RecognitionStatus.SENT;
                    }).catch((reason) => {
                        this.tributeStatus = TributeStatus.INITIALIZED;
                    });
                },
                onTributeSubmit: function (event): void {
                    event.preventDefault();
                    this.tributeStatus = TributeStatus.SENDING;

                    createRequest(this.tribute_endpoint, {
                        'tribute_switch': this.tributeSwitch,
                        'form': {
                            'tribute': this.tribute,
                            'tribute_name': this.tributeName,
                            'tribute_address': this.tributeAddress,
                        }
                    }).then((data) => {
                        this.tributeStatus = TributeStatus.SENT;
                    }).catch((reason) => {
                        this.tributeStatus = TributeStatus.INITIALIZED;
                    });
                },
                onReasonSubmit: function (event): void {
                    event.preventDefault();
                    this.reasonStatus = ReasonStatus.SENDING;

                    createRequest(this.reason_endpoint, {
                        'form': {
                            'donation-reason': this.donationReason,
                        }
                    }).then((data) => {
                        this.reasonStatus = ReasonStatus.SENT;
                    }).catch((reason) => {
                        this.reasonStatus = ReasonStatus.INITIALIZED;
                    });
                },
                resetRecognitionStatus: function () {
                    return this.recognitionStatus = RecognitionStatus.INITIALIZED;
                },
                resetTributeStatus: function () {
                    return this.tributeStatus = TributeStatus.INITIALIZED;
                },
                resetReasonStatus: function () {
                    return this.reasonStatus = ReasonStatus.INITIALIZED;
                },
                resetOrderStatus: function () {
                    return this.orderStatus = OrderStatus.INITIALIZED;
                }
            }
        })

        window.fetch(window['DJANGO']['boDomain'].replace(/\/en/, '/' + currentLang) + "/opg-api/config", {
            method: "GET",
            credentials: "omit",
            mode: "cors"
        })
            .then((response) => {
                if (response.status >= 200 && response.status < 300) {
                    return response.json();
                }
            })
            .then((data: {
                ApplePayConfig: ApplePayConfig,
                PayPalConfig: PayPalConfig,
                texts: any[],
                recognition_endpoint: string,
                tribute_endpoint: string,
                reason_endpoint: string,
            }) => {
                if (data.texts) {
                    this.texts = data.texts;
                    app.setTexts(this.texts);
                }

                if (data.recognition_endpoint) {
                    this.recognition_endpoint = data.recognition_endpoint;
                    app.setRecognitionEndpoint(this.recognition_endpoint);
                }

                if (data.tribute_endpoint) {
                    this.tribute_endpoint = data.tribute_endpoint;
                    app.setTributeEndpoint(this.tribute_endpoint);
                }

                if (data.reason_endpoint) {
                    this.reason_endpoint = data.reason_endpoint;
                    app.setReasonEndpoint(this.reason_endpoint);
                }

                if (data.ApplePayConfig) {
                    this.applePay = new ApplePay({
                        ...{
                            callbacks: {
                                paymentAuthorized: {
                                    start: app.onAuthorizePayment,
                                    success: app.onAuthorizePaymentSuccess,
                                    fail: app.onAuthorizePaymentFail,
                                    error: app.onAuthorizePaymentError
                                },
                                payment: app.onPayment,
                                cancel: app.onCancel
                            }
                        },
                        ...data.ApplePayConfig
                    });


                    app.setApplePayInstance(this.applePay);
                }

                if (data.ApplePayConfig) {
                    this.payPal = new PayPal({
                        ...{
                            callbacks: {
                                authorize: {
                                    start: app.onAuthorizePayment,
                                    success: app.onAuthorizePaymentSuccess,
                                    fail: app.onAuthorizePaymentFail,
                                    error: app.onAuthorizePaymentError
                                },
                                payment: app.onPayment,
                                cancel: app.onCancel,
                                error: app.onError,
                            }
                        },
                        ...data.PayPalConfig
                    });

                    app.setPayPalInstance(this.payPal);
                }

                app.$mount(this.el)
                this.payPal.init();
            })
            .catch((reason) => {
                console.log("Failed to load One Page Giving config")
            })
    }
}




// WEBPACK FOOTER //
// ./@modules/views/one-page-giving-widget-view.ts